import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { queryKeys } from "@/hooks/queryKeys";
import { useUiStore } from "@/stores/ui.store";
import { isValidEmail } from "@/lib/validation";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Card, CardContent } from "@/components/ui/Card";
import { Typography } from "@/components/ui/Typography";

interface Unit {
  id: string;
  alias: string;
  unitNumber: string;
}

export function AddTenantPage() {
  const params = useParams();
  const navigate = useNavigate();
  const propertyId = params.id as string;
  const queryClient = useQueryClient();
  const addToast = useUiStore((s) => s.addToast);
  const [searchParams] = useSearchParams();
  const preselectedUnitId = searchParams.get("unitId");

  const [propertyName, setPropertyName] = useState("");
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [form, setForm] = useState({
    unitId: "",
    name: "",
    phone: "",
    email: "",
  });

  useEffect(() => {
    async function load() {
      try {
        const prop = await api.get<{ name: string; units: Unit[] }>(`/properties/${propertyId}`);
        setPropertyName(prop.name);
        const list = prop.units ?? [];
        setUnits(list);
        if (preselectedUnitId && list.some((u) => u.id === preselectedUnitId)) {
          setForm((f) => ({ ...f, unitId: preselectedUnitId }));
        }
      } catch {
        addToast({ type: "error", message: "Failed to load property" });
        navigate("/properties");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [propertyId, navigate, addToast, preselectedUnitId]);

  function update(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrors({});

    const newErrors: Record<string, string> = {};
    if (!form.unitId) newErrors.unitId = "Please select a unit";
    if (!form.name.trim()) newErrors.name = "Tenant name is required";
    if (!form.phone.trim() || form.phone.length < 10) newErrors.phone = "Valid phone number is required";
    if (form.email.trim() && !isValidEmail(form.email)) newErrors.email = "Invalid email address";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/tenants", {
        unitId: form.unitId,
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.propertyDetail(propertyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.properties() });
      queryClient.invalidateQueries({ queryKey: queryKeys.unitDetail(form.unitId) });
      addToast({ type: "success", message: "Tenant added" });
      navigate(`/property/${propertyId}/unit/${form.unitId}`);
    } catch (err) {
      addToast({
        type: "error",
        message: err instanceof ApiError ? err.message : "Failed to add tenant",
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-64 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse" />
        <div className="h-64 bg-slate-200 dark:bg-slate-700 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (units.length === 0) {
    return (
      <div className="space-y-6 max-w-2xl">
        <div>
          <Link
            to={`/property/${propertyId}`}
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-2"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
            Back to {propertyName}
          </Link>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-center">
          <p className="text-amber-800 font-medium">No units yet</p>
          <p className="mt-1 text-sm text-amber-700">
            Add a unit first, then you can add tenants to it.
          </p>
          <Link
            to={`/property/${propertyId}/units/new`}
            className="mt-4 inline-block text-sm font-medium text-amber-700 hover:text-amber-800"
          >
            Add unit →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <Link
          to={`/property/${propertyId}`}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-2"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
          </svg>
          Back to {propertyName}
        </Link>
        <Typography variant="h1">Add Tenant</Typography>
        <p className="mt-1 text-sm text-slate-500">Add a tenant to a unit in {propertyName}</p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="space-y-4">
            <Select
              label="Unit"
              options={[{ value: "", label: "Select unit" }, ...units.map((u) => ({ value: u.id, label: `${u.alias} (${u.unitNumber})` }))]}
              value={form.unitId}
              onChange={(e) => update("unitId", e.target.value)}
              error={errors.unitId}
            />
            <Input
              label="Name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              error={errors.name}
              required
            />
            <Input
              label="Phone"
              type="tel"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              error={errors.phone}
              required
            />
            <Input
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="Optional"
            />
          </CardContent>
        </Card>

        <div className="flex gap-3 mt-6">
          <Link to={`/property/${propertyId}`}>
            <Button variant="secondary" type="button">
              Cancel
            </Button>
          </Link>
          <Button type="submit" isLoading={submitting}>
            Add Tenant
          </Button>
        </div>
      </form>
    </div>
  );
}
